import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Target,
  ArrowRight,
  Check,
  Zap,
  Sparkles,
  Globe,
  Shield,
  TrendingUp,
  Mail,
  Database,
  Brain,
  PlayCircle,
  Workflow,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import TryItWidget from "@/components/TryItWidget";
import AffiliateBanner from "@/components/AffiliateBanner";
import HowItWorks from "@/components/HowItWorks";
import SeoHead from "@/components/seo/SeoHead";
import { breadcrumb, faqPage } from "@/components/seo/schemas";

const STATS = [
  { value: "85 ÷ 25", label: "Same opportunity, different fit", sub: "Score precision unmatched at this price" },
  { value: "~$0.10", label: "per fully-enriched lead", sub: "vs $25 in SDR manual research" },
  { value: "<2 min", label: "ICP setup", sub: "Website + 3 questions" },
  { value: "30 days", label: "full Apex trial", sub: "Every feature unlocked · no card" },
];

const INTEL_FEATURES = [
  { icon: Database, title: "Tech stack signals", body: "Cloud, CRM, security & infra tools — extracted live from web research." },
  { icon: TrendingUp, title: "Real-time intent", body: "Hiring, news, funding, partnerships — surfaced as concrete opening hooks." },
  { icon: Mail, title: "Verified contacts", body: "Multi-layer email validation. Directory match + deliverability check + smart fallback." },
  { icon: Brain, title: "Buying committee", body: "Identifies decision-makers, surfaces their roles & rationale." },
  { icon: Zap, title: "Engagement playbook", body: "Timing window + concrete first-line opener + value-prop tied to their stack." },
  { icon: Sparkles, title: "Personalized fit", body: "Scored 0-100 against YOUR ICP — not a generic industry benchmark." },
];

const OUTREACH_BULLETS = [
  { icon: Sparkles, title: "AI drafts from enrichment data", body: "One click on any lead → a personalized cold email referencing their tech stack, hiring signals, and recent news. Not Mad-Libs. Real reasoning." },
  { icon: Mail, title: "BYO inbox (Gmail / Outlook / any SMTP)", body: "All emails send from your address. Replies, deliverability, and sender reputation stay yours. We never touch your domain." },
  { icon: Workflow, title: "Multi-step sequences with auto-pause", body: "Schedule 3-7 follow-ups across days/weeks. Replies, bounces, and unsubscribes auto-pause the sequence — no embarrassing 'just bumping this' after they said yes." },
  { icon: TrendingUp, title: "Open / click / reply tracking", body: "Pixel + tracked links + IMAP reply matching. Replies are inferred as opens so corporate firewalls can't hide engagement." },
  { icon: Shield, title: "Sales hygiene baked-in", body: "Cross-sequence guardrail (one active sequence per lead) + configurable cooldown after exits. Prevents flooding fresh exits." },
  { icon: Database, title: "Pay-as-you-go, never expires", body: "$5 buys 100 email credits. 1 credit = 1 AI draft. Editing, sending, and tracking are free. Credits roll forever." },
];

const FAQ = [
  {
    q: "Is this just a ChatGPT wrapper?",
    a: "No. ChatGPT alone has no live company data — it would hallucinate hiring signals or invent funding events. LeadIQ orchestrates live web research, licensed B2B data, and our proprietary AI pipeline calibrated for B2B sales tasks. The AI is one layer; the moat is the pipeline plus the personalized fit-scoring engine.",
  },
  {
    q: "Where does your data come from?",
    a: "We aggregate from licensed B2B data partners, structured web research, and trusted public sources. Every enrichment is run fresh — no stale cached data. Like Apollo, ZoomInfo, and Cognism, we work with multiple sources under strict compliance terms. The differentiator isn't which sources we use — it's how our AI synthesizes them.",
  },
  {
    q: "How does fit-scoring know what I sell?",
    a: "You paste your website + answer 3 quick questions about your ICP (target industries, company size, top pain). We scan your site, extract your positioning, and from then on every prospect gets scored against YOUR offering. Every score includes a transparent rationale — no black box.",
  },
  {
    q: "How accurate is it?",
    a: "Opportunity scores tie back to concrete signals (named hires, dated news, tech stack evidence). Email accuracy is multi-layer: directory match + deliverability check + smart fallback. Fit scores show the reasoning behind every number so your team can challenge a verdict.",
  },
  {
    q: "Will this work for my industry?",
    a: "If your prospects have a website, news coverage, or LinkedIn presence — yes. We've validated against B2B SaaS, fintech, healthcare, manufacturing, logistics, and professional services. Pure consumer/D2C is a weaker fit (their signals are different).",
  },
  {
    q: "How does AI Outreach work — and why isn't it locked behind a higher tier?",
    a: "Every plan (including the 30-day free trial) gets the full AI Outreach feature: connect your own Gmail/Outlook/SMTP, draft personalized cold emails from any lead's enrichment in one click, build multi-step sequences with auto-pause on reply, and track opens/clicks/replies in your dashboard. We charge for email drafts as pay-as-you-go credits ($5 = 100 drafts, credits never expire). Apollo bundles outreach into $49/user/mo seats — we keep it usage-based so you only pay for what you send.",
  },
  {
    q: "Can I switch plans later?",
    a: "Anytime — and pro-rated. Upgrades take effect instantly; downgrades apply at the next billing cycle. No annual lock-in unless you choose the annual option (10% discount).",
  },
];

export default function Landing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    // Ensure autoplay on iOS/mobile by setting muted + playsinline
    if (videoRef.current) videoRef.current.play().catch(() => {});
  }, []);

  const primaryCta = user ? "Go to dashboard" : "Start 30-day free trial";
  const primaryHref = user ? "/dashboard" : "/register";

  const handleLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#fafaf7] text-ink-900" data-testid="landing-page">
      <SeoHead
        title="LeadIQ Pro — AI-powered B2B Lead Enrichment Engine"
        description="Turn raw lead lists into enriched intelligence packages. Verified work emails + mobile numbers + personalised ICP fit scoring + buying intent signals. Only pay for verified hits."
        path="/"
        jsonLd={[
          breadcrumb([{ name: "Home", url: "/" }]),
          faqPage([
            {
              q: "What is LeadIQ Pro?",
              a: "LeadIQ Pro is an AI-powered B2B lead enrichment engine. Upload a CSV, paste a single contact, or hit the REST API — we return verified work emails, mobile numbers, company technographics, buying-intent signals (hiring, funding, tech-stack), and a 0–100 ICP fit score tailored to your product.",
            },
            {
              q: "How does the waterfall enrichment work?",
              a: "Our AI router analyses each lead's domain, geography and seniority, then builds the optimal provider sequence and cascades through vetted sources. Every result passes a 4-layer validator (syntax, MX, SMTP, catch-all for email; carrier, HLR, live-ping for mobile). Failed layers are skipped and never charged.",
            },
            {
              q: "Do I pay for invalid hits?",
              a: "No. You only pay for verified data. If a result fails any validator layer, we skip it and move to the next provider — no credit is deducted.",
            },
            {
              q: "What is the ICP fit score?",
              a: "It is a 0–100 score computed against your offering — not a generic industry benchmark. Each enriched lead is sorted into P1 (hot — fit + live buying signal), P2 (warm — right ICP, no signal), or P3 (drop) so reps can open the right list every day.",
            },
            {
              q: "Which regions and languages does LeadIQ Pro cover?",
              a: "LeadIQ Pro operates worldwide and supports English. Enrichment coverage spans global work-email and mobile databases across every B2B industry.",
            },
            {
              q: "Is there a free trial?",
              a: "Yes — a 30-day full-access trial with no credit card required. You get live enrichment credits so you can test the product on your own list before paying.",
            },
          ]),
        ]}
      />
      <AffiliateBanner />
      {/* ───── Top bar ───── */}
      <header className="sticky top-0 z-40 bg-[#fafaf7]/95 border-b border-ink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo-128.png"
              alt="LeadIQ Pro"
              width="36"
              height="36"
              className="w-9 h-9 shrink-0"
            />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-base tracking-tight">
                LeadIQ Pro
              </div>
              <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
                Lead Enrichment Engine
              </div>
            </div>
          </Link>
          <nav className="flex items-center gap-5">
            <Link
              to="/pricing"
              className="text-xs uppercase tracking-widestx font-semibold text-ink-500 hover:text-ink-900 transition-colors"
              data-testid="landing-nav-pricing"
            >
              Pricing
            </Link>
            <Link
              to="/developers"
              className="text-xs uppercase tracking-widestx font-semibold text-ink-500 hover:text-ink-900 transition-colors hidden md:inline-block"
              data-testid="landing-nav-developers"
            >
              Developers
            </Link>
            <Link
              to={user ? "/dashboard" : "/login"}
              className="text-xs uppercase tracking-widestx font-semibold text-ink-500 hover:text-ink-900 transition-colors hidden sm:inline-block"
              data-testid="landing-nav-login"
            >
              {user ? "Dashboard" : "Sign in"}
            </Link>
            {user && (
              <button
                type="button"
                onClick={handleLogout}
                data-testid="landing-nav-logout"
                className="text-xs uppercase tracking-widestx font-semibold text-[#D92D20] hover:text-[#9F1F17] transition-colors hidden sm:inline-flex items-center gap-1"
              >
                <LogOut className="w-3 h-3" />
                Sign out
              </button>
            )}
            <Link
              to={primaryHref}
              className="inline-flex items-center gap-1.5 bg-ink-900 hover:bg-ink-700 text-white text-[11px] uppercase tracking-widestx font-bold px-3 py-2 rounded-sm transition-colors"
              data-testid="landing-nav-cta"
            >
              {primaryCta}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </nav>
        </div>
      </header>

      {/* ───── HERO ───── */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative z-10">
            <div className="inline-flex items-center gap-1.5 bg-ink-100 text-ink-700 text-[10px] uppercase tracking-widestx font-bold px-2.5 py-1.5 rounded-sm">
              <Sparkles className="w-2.5 h-2.5" />
              AI-native · personalized fit scoring
            </div>
            <h1 className="mt-5 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Lead intelligence,
              <br />
              <span className="text-brand">at scale.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-ink-500 max-w-md leading-relaxed">
              We research every company to score opportunity AND personalized fit
              against YOUR offering and hand out a intelligent sales ready package — in
              seconds.
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link
                to={primaryHref}
                data-testid="landing-hero-cta"
                className="inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-700 text-white font-semibold px-5 py-3 rounded-sm text-sm transition-colors"
              >
                {primaryCta}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#demo"
                data-testid="landing-watch-demo"
                className="inline-flex items-center gap-2 text-ink-700 hover:text-ink-900 font-semibold text-sm"
              >
                <PlayCircle className="w-4 h-4" />
                Watch 30-sec demo
              </a>
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-widestx font-mono text-ink-400 flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3 h-3 text-[#2E7D32]" />
                No card required
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3 h-3 text-[#2E7D32]" />
                30-day trial
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3 h-3 text-[#2E7D32]" />
                Cancel anytime
              </span>
            </div>
          </div>

          {/* Video */}
          <div className="lg:col-span-7 relative">
            <div className="relative rounded-sm overflow-hidden border border-ink-100 shadow-2xl shadow-ink-900/10">
              <video
                ref={videoRef}
                data-testid="landing-hero-video"
                src="/demo/leadiq_demo.mp4"
                poster="/landing/leads_dual_scores.png"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                disablePictureInPicture
                className="w-full h-auto block"
              />
            </div>
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-ink-900/75 backdrop-blur text-white text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5252] animate-pulse" />
              Live demo
            </div>
            {/* removed heavy decorative blur — was forcing repaints on scroll */}
          </div>
        </div>

        {/* Stats strip */}
        <div className="border-y border-ink-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="border-l-2 border-ink-100 pl-4 first:border-l-0 first:pl-0">
                <div className="font-display font-black text-2xl tracking-tighter leading-none">
                  {s.value}
                </div>
                <div className="text-xs font-semibold text-ink-900 mt-1.5">{s.label}</div>
                <div className="text-[10px] text-ink-400 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── TRY-IT WIDGET (top-of-funnel) ───── */}
      <TryItWidget />

      {/* ───── HOW IT WORKS · 5-step pipeline ───── */}
      <HowItWorks primaryHref={primaryHref} />

      {/* ───── DUAL SCORES (the differentiator) ───── */}
      <section
        id="differentiator"
        className="bg-ink-900 text-white py-20 sm:py-28"
        data-testid="landing-dual-scores"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-white/40">
              The one thing no one else does at this price
            </div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Two scores.
              <br />
              <span className="text-brand">Not one.</span>
            </h2>
            <p className="mt-5 text-base text-white/70 leading-relaxed max-w-md">
              Generic intent scores tell you a company is hot. They don't tell you if{" "}
              <span className="text-white font-semibold">that</span> company is right for{" "}
              <span className="text-white font-semibold">you</span>. LeadIQ scores
              both — so your SDRs stop chasing wrong-fit prospects.
            </p>
            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-3">
                <div className="grid place-items-center w-7 h-7 rounded-sm bg-white/10 shrink-0">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Opportunity score</div>
                  <div className="text-xs text-white/60">
                    Live signals: hiring, funding, news, tech stack
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="grid place-items-center w-7 h-7 rounded-sm bg-brand shrink-0">
                  <Target className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Fit score · personalized</div>
                  <div className="text-xs text-white/60">
                    Scored against YOUR offering with a transparent rationale
                  </div>
                </div>
              </div>
            </div>
            <Link
              to={primaryHref}
              className="mt-8 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-3 rounded-sm text-sm transition-colors"
              data-testid="landing-dual-cta"
            >
              See it for free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 relative">
            <img
              src="/landing/leads_dual_scores.png"
              alt="Lead cards showing opportunity and fit scores"
              loading="lazy"
              decoding="async"
              className="w-full rounded-sm border border-white/10 shadow-2xl shadow-black/40"
            />
          </div>
        </div>
      </section>

      {/* ───── INTELLIGENCE PACKAGE ───── */}
      <section className="py-20 sm:py-28" data-testid="landing-intel-package">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              What you get per lead
            </div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Every lead, a
              <br />
              complete dossier.
            </h2>
            <p className="mt-5 text-base text-ink-500 max-w-lg leading-relaxed">
              Not a contact card. A full intelligence package — the kind your SDRs would
              build manually in 15 minutes, ready in seconds.
            </p>
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 relative">
              <img
                src="/landing/leadcard.png"
                alt="Expanded LeadCard showing complete intelligence package"
                loading="lazy"
                decoding="async"
                className="w-full rounded-sm border border-ink-100 shadow-xl shadow-ink-900/5"
              />
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-1 gap-1">
                {INTEL_FEATURES.map((f) => (
                  <div
                    key={f.title}
                    className="flex items-start gap-3 p-3 rounded-sm hover:bg-ink-100/60 transition-colors"
                  >
                    <div className="grid place-items-center w-8 h-8 rounded-sm bg-ink-100 shrink-0">
                      <f.icon className="w-4 h-4 text-ink-700" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-ink-900">{f.title}</div>
                      <div className="text-xs text-ink-500 mt-0.5 leading-relaxed">
                        {f.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── 3-STEP WORKFLOW (data → draft → send) ───── */}
      <section className="py-20 sm:py-28 bg-ink-50/40" data-testid="landing-workflow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              The whole loop
            </div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              From data to outbox
              <br />
              in <span className="text-brand">3 clicks.</span>
            </h2>
            <p className="mt-5 text-base text-ink-500 max-w-lg leading-relaxed">
              Most enrichment tools hand you a spreadsheet and wave goodbye. We hand you a
              workflow — research, write, and send without ever leaving the app.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 lg:gap-3 relative">
            {[
              {
                step: "01",
                icon: Database,
                title: "Enrich",
                body: "Upload a CSV or paste a single domain. We pull tech stack, hiring signals, recent news, decision-makers, and verified emails — synthesized into a dossier per lead.",
                testid: "workflow-step-enrich",
              },
              {
                step: "02",
                icon: Sparkles,
                title: "Draft",
                body: "Click \u201cDraft\u201d on any lead card. The AI reads the full enrichment and writes a personalized cold email referencing their actual stack, hires, or news. Edit any line. 1 credit.",
                testid: "workflow-step-draft",
              },
              {
                step: "03",
                icon: Mail,
                title: "Send &amp; track",
                body: "Sends through your own Gmail / Outlook / SMTP — your domain, your reputation. Opens, clicks, and replies land back in your LeadIQ dashboard. Sequences auto-pause on reply.",
                testid: "workflow-step-send",
              },
            ].map((s, i) => (
              <div
                key={s.step}
                data-testid={s.testid}
                className="bg-white border border-ink-100 rounded-sm p-6 relative hover:border-ink-300 transition-colors group"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="font-mono text-[10px] uppercase tracking-widestx text-ink-400">
                    Step {s.step}
                  </div>
                  <div className="grid place-items-center w-9 h-9 rounded-sm bg-brand/10 text-brand group-hover:bg-brand group-hover:text-white transition-colors">
                    <s.icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="font-display font-extrabold text-2xl text-ink-900 mb-2"
                     dangerouslySetInnerHTML={{ __html: s.title }}
                />
                <p className="text-sm text-ink-500 leading-relaxed">{s.body}</p>
                {/* connector arrow on md+ between cards 1→2, 2→3 */}
                {i < 2 && (
                  <div className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-ink-900 text-white items-center justify-center text-xs pointer-events-none">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 text-center text-[12px] text-ink-500">
            Editing, re-sending, opens, clicks, and replies are all <strong className="text-ink-900">free</strong>.
            You only pay 1 credit per AI draft.
          </div>
        </div>
      </section>

      {/* ───── AI OUTREACH (from enrichment → outbox) ───── */}
      <section
        className="bg-ink-900 text-white py-20 sm:py-28 relative overflow-hidden"
        data-testid="landing-ai-outreach"
      >
        {/* subtle accent glow */}
        <div
          className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-brand/15 blur-3xl pointer-events-none"
          aria-hidden
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-1.5 bg-brand/15 text-brand text-[10px] uppercase tracking-widestx font-bold px-2.5 py-1.5 rounded-sm mb-5">
                <Sparkles className="w-2.5 h-2.5" />
                AI Outreach · included
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
                Don&apos;t just enrich —
                <br />
                <span className="text-brand">send.</span>
              </h2>
              <p className="mt-5 text-base text-white/70 leading-relaxed max-w-md">
                Most enrichment tools stop at data. We turn every dossier into
                an actual personalized email — drafted, sent, and tracked from your inbox.
                Apollo charges $49/user/mo for this. We charge $5 / 100 emails.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  onClick={() => nav(primaryHref)}
                  data-testid="outreach-cta-start"
                  className="bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-sm text-sm inline-flex items-center gap-2 transition-colors"
                >
                  Start free 30-day trial
                  <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/pricing"
                  className="text-white/70 hover:text-white font-medium px-4 py-3 rounded-sm text-sm inline-flex items-center gap-2 transition-colors"
                >
                  See email credit packs
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-6 text-[11px] font-mono text-white/40">
                Gmail · Outlook · Zoho · Hostinger · any SMTP
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-3">
                {OUTREACH_BULLETS.map((b) => (
                  <div
                    key={b.title}
                    className="bg-white/[0.04] border border-white/10 rounded-sm p-4 hover:bg-white/[0.07] hover:border-white/20 transition-colors"
                  >
                    <div className="grid place-items-center w-8 h-8 rounded-sm bg-brand/15 mb-3">
                      <b.icon className="w-4 h-4 text-brand" />
                    </div>
                    <div className="text-sm font-semibold text-white mb-1">
                      {b.title}
                    </div>
                    <div className="text-xs text-white/60 leading-relaxed">
                      {b.body}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── ICP SETUP ───── */}
      <section
        className="bg-white border-y border-ink-100 py-20 sm:py-28"
        data-testid="landing-icp-setup"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <img
              src="/landing/icp_wizard.png"
              alt="ICP setup wizard"
              loading="lazy"
              decoding="async"
              className="w-full rounded-sm border border-ink-100 shadow-xl shadow-ink-900/5"
            />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Setup · 2 minutes
            </div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Teach the AI
              <br />
              <span className="text-brand">what you sell.</span>
            </h2>
            <p className="mt-5 text-base text-ink-500 max-w-md leading-relaxed">
              Paste your website. Answer 3 questions. Done. Every enrichment after that
              is scored against YOUR ICP — and you can re-tune anytime.
            </p>
            <ol className="mt-8 space-y-4">
              {[
                ["1", "Paste your URL", "We scan your homepage + about page and extract positioning, value props, and what you sell."],
                ["2", "Answer 3 questions", "Target industries · typical company size · top pain you solve."],
                ["3", "Get personalized scores", "Every lead, scored 0-100 against your ICP with a transparent rationale."],
              ].map(([n, title, body]) => (
                <li key={n} className="flex items-start gap-4">
                  <div className="grid place-items-center w-8 h-8 rounded-sm bg-ink-900 text-white font-mono font-bold text-xs shrink-0">
                    {n}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-ink-900">{title}</div>
                    <div className="text-xs text-ink-500 mt-0.5 leading-relaxed">
                      {body}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ───── TIER STORY ───── */}
      <section className="py-20 sm:py-28" data-testid="landing-tiers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Intelligence depth, scaled to your needs
            </div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Atom. Spark. Apex.
            </h2>
            <p className="mt-5 text-base text-ink-500 max-w-lg leading-relaxed">
              Three proprietary AI models, each calibrated for a different stage of
              sales sophistication. Upgrade depth as you scale.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { name: "Atom", price: "$0 – $29", desc: "Fast, accurate baseline for pipeline volume.", best: "Solo SDRs" },
              { name: "Spark", price: "$149", desc: "5× sharper analysis + LeadIQ email verification + fit scoring.", best: "Sales teams", highlight: true },
              { name: "Apex", price: "$599", desc: "Frontier-grade reasoning + premium outreach lines.", best: "Revenue teams" },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`p-7 rounded-sm transition-transform hover:-translate-y-1 ${
                  tier.highlight
                    ? "bg-ink-900 text-white"
                    : "bg-white border border-ink-100"
                }`}
              >
                <div
                  className={`text-[10px] uppercase tracking-widestx font-mono ${
                    tier.highlight ? "text-white/60" : "text-ink-400"
                  }`}
                >
                  LeadIQ
                </div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">
                  {tier.name}
                </div>
                <div
                  className={`font-mono text-sm mt-3 ${
                    tier.highlight ? "text-white/80" : "text-ink-700"
                  }`}
                >
                  {tier.price}
                  <span className={`text-[10px] ml-1 ${tier.highlight ? "text-white/40" : "text-ink-400"}`}>
                    /mo
                  </span>
                </div>
                <p
                  className={`text-sm mt-4 leading-relaxed ${
                    tier.highlight ? "text-white/70" : "text-ink-500"
                  }`}
                >
                  {tier.desc}
                </p>
                <div
                  className={`mt-5 text-[10px] uppercase tracking-widestx font-bold ${
                    tier.highlight ? "text-brand" : "text-ink-700"
                  }`}
                >
                  Best for · {tier.best}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/pricing"
              data-testid="landing-tiers-cta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-ink-700 hover:text-ink-900"
            >
              See full pricing & feature comparison
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───── TRUST BAR ───── */}
      <section className="bg-ink-900 text-white py-16" data-testid="landing-trust">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-4 gap-6">
          {[
            { icon: Shield, label: "GDPR & CCPA", sub: "Compliant data sources" },
            { icon: Database, label: "Encrypted", sub: "In transit & at rest" },
            { icon: Globe, label: "Multi-source", sub: "Provider-agnostic by design" },
            { icon: Mail, label: "Audit logs", sub: "Every change tracked" },
          ].map((t) => (
            <div key={t.label} className="flex items-start gap-3">
              <div className="grid place-items-center w-9 h-9 rounded-sm bg-white/10 shrink-0">
                <t.icon className="w-4 h-4 text-brand" />
              </div>
              <div>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-xs text-white/50 mt-0.5">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───── PROOF / CASE STUDIES TEASER ───── */}
      <section className="py-20 sm:py-28 bg-ink-50/40" data-testid="landing-proof">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_auto] items-end gap-8 mb-12">
            <div>
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Customer outcomes
              </div>
              <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05] text-ink-900">
                Real workflows.<br />
                <span className="font-light">Real numbers.</span>
              </h2>
              <p className="mt-4 text-ink-500 max-w-xl text-sm sm:text-base leading-relaxed">
                How B2B teams use LeadIQ Pro to shorten outbound cycles, lift connect rates
                and book more qualified meetings — anonymized at customer request.
              </p>
            </div>
            <Link
              to="/case-studies"
              data-testid="landing-proof-see-all"
              className="hidden lg:inline-flex items-center gap-2 text-[11px] uppercase tracking-widestx font-semibold text-ink-900 hover:text-emerald-800 border-b border-ink-900 hover:border-emerald-800 pb-0.5 self-end"
            >
              See all stories <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                tag: "SaaS · Series B",
                metric: "42%",
                sub: "higher outbound reply rate",
                quote:
                  "Bulk mobile discovery cut prospecting time in half. Reps hit quota every week without babysitting the CRM.",
                role: "Head of Sales · 51–200 emp.",
                feature: "Bulk Mobile",
              },
              {
                tag: "Fintech · Growth stage",
                metric: "3.1x",
                sub: "meetings booked per SDR / week",
                quote:
                  "The ICP fit score means our SDRs stop chasing bad-fit leads. We book more, and the ones we book actually close.",
                role: "VP of Revenue · 201–1,000 emp.",
                feature: "ICP Fit Scoring",
              },
              {
                tag: "MarTech · Agency",
                metric: "68%",
                sub: "email deliverability lift",
                quote:
                  "The verified-email cascade dropped our bounce rate from 22% to under 3%. Outbound is finally reliable again.",
                role: "Marketing Ops Lead · 1–50 emp.",
                feature: "Bulk Email",
              },
            ].map((c) => (
              <Link
                to="/case-studies"
                key={c.tag}
                data-testid={`landing-proof-card-${c.tag}`}
                className="group bg-white border border-ink-100 rounded-sm p-6 flex flex-col gap-4 hover:border-ink-900 transition-colors"
              >
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widestx font-mono text-ink-500">
                  <TrendingUp className="w-3.5 h-3.5 text-ink-700" />
                  <span>{c.tag}</span>
                </div>
                <div>
                  <div className="font-display text-5xl leading-none font-extrabold text-ink-900">
                    {c.metric}
                  </div>
                  <div className="text-xs text-ink-500 mt-2">{c.sub}</div>
                </div>
                <p className="text-[13px] leading-relaxed text-ink-700 border-l-2 border-emerald-500 pl-3">
                  "{c.quote}"
                </p>
                <div className="mt-auto pt-3 border-t border-ink-100 flex items-center justify-between gap-2">
                  <div className="text-[10px] text-ink-500 font-mono">{c.role}</div>
                  <span className="text-[10px] uppercase tracking-widestx font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">
                    {c.feature}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex justify-center lg:hidden">
            <Link
              to="/case-studies"
              data-testid="landing-proof-see-all-mobile"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widestx font-semibold text-white bg-ink-900 hover:bg-ink-700 px-5 h-11 rounded-sm"
            >
              See all stories <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="py-20 sm:py-28" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Common questions
            </div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Things buyers ask.
            </h2>
          </div>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <details
                key={i}
                data-testid={`landing-faq-${i}`}
                className="group bg-white border border-ink-100 rounded-sm p-5 cursor-pointer"
              >
                <summary className="flex items-center justify-between gap-3 list-none">
                  <span className="font-semibold text-ink-900 text-sm">{f.q}</span>
                  <span className="text-ink-400 group-open:rotate-45 transition-transform text-xl leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-ink-500 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="bg-brand py-20 sm:py-28 text-white" data-testid="landing-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
            Stop chasing wrong-fit leads.
          </h2>
          <p className="mt-5 text-base text-white/80 max-w-xl mx-auto">
            30-day free trial — full Apex tier, no credit card. Set up your ICP in 2 minutes. Cancel anytime.
          </p>
          <Link
            to={primaryHref}
            data-testid="landing-final-cta-btn"
            className="mt-8 inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-700 text-white font-semibold px-7 py-4 rounded-sm transition-colors"
          >
            {primaryCta}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ───── Footer ───── */}
      <footer className="bg-ink-900 text-white/60 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/logo-white-64.png"
              alt="LeadIQ Pro"
              width="28"
              height="28"
              className="w-7 h-7 shrink-0"
            />
            <div className="text-xs font-mono">
              LeadIQ Pro · © {new Date().getFullYear()}
            </div>
          </div>
          <div className="flex items-center gap-5 text-[10px] uppercase tracking-widestx font-semibold flex-wrap">
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/developers" className="hover:text-white transition-colors" data-testid="footer-developers">Developers</Link>
            <Link to="/case-studies" className="hover:text-white transition-colors" data-testid="footer-case-studies">Case studies</Link>
            <Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/legal/refunds" className="hover:text-white transition-colors" data-testid="footer-refunds">Refunds</Link>
            <Link to="/legal/do-not-sell" className="hover:text-white transition-colors" data-testid="footer-do-not-sell">Do not sell</Link>
            <Link to={user ? "/dashboard" : "/login"} className="hover:text-white transition-colors">
              {user ? "Dashboard" : "Sign in"}
            </Link>
            {user && (
              <button
                type="button"
                onClick={handleLogout}
                data-testid="landing-footer-logout"
                className="text-[#F87171] hover:text-white transition-colors uppercase inline-flex items-center gap-1"
              >
                <LogOut className="w-3 h-3" />
                Sign out
              </button>
            )}
            <button
              onClick={() => nav(primaryHref)}
              className="hover:text-white transition-colors uppercase"
            >
              Get started
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
